import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, ArrowLeft } from 'lucide-react';
import { debug } from '@/utils/debug';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);

    if (error) {
      // Treat webhook failures as soft success to avoid user enumeration
      // and improve UX when email service has issues
      if (error.message?.includes("unexpected_failure") || 
          error.message?.includes("status code returned from hook")) {
        debug.warn("Email service issue, showing success message:", error);
        setEmailSent(true);
        toast({
          title: "Check Your Email",
          description: "If an account exists with this email, you'll receive reset instructions",
        });
        return;
      }
      
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setEmailSent(true);
    toast({
      title: "Check Your Email",
      description: "We've sent you a password reset link",
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 charcoal-pro">
      <Card className="w-full max-w-md p-6 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Reset Password</h1>
          <p className="text-muted-foreground">
            {emailSent 
              ? "Check your email for the reset link" 
              : "Enter your email to receive a reset link"}
          </p>
        </div>

        {!emailSent ? (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>
        ) : (
          <div className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <p className="text-xs text-muted-foreground">
              Didn't receive it? Check your spam folder or try again.
            </p>
          </div>
        )}

        <div className="text-center">
          <button
            type="button"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
            onClick={(e) => {
              e.preventDefault();
              navigate('/auth');
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>
        </div>
      </Card>
    </div>
  );
}
